<script setup>
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { ref, computed } from 'vue';
import { ListNumbersIcon, EditIcon, TrashIcon, PlusIcon, PlayerPlayIcon, CodeIcon } from 'vue-tabler-icons';
import CodeEditor from '@/components/CodeEditor.vue';
import EditChoice from './EditChoice.vue';
import Init from './Init.vue';
import SampleUI from './SampleUI.vue';

const store = useStore();
const route = useRoute();

const draft = computed(() => store.getters['ci/currentCIDraft']).value;
const choices = ref(draft?.choices || []);
const expanded = ref({ choice: null, behavior: null });

const remove = (removedChoice) => {
  store.commit('ci/setPreviewerOperation', {
    operations: [
      {
        component: 'choices',
        operation: 'DELETE',
        details: {
          choice: {
            name: removedChoice.name,
            type: removedChoice.type,
            default: removedChoice.default,
            behaviors: removedChoice.behaviors,
          },
        },
      },
    ],
    ciId: route.params.ciId,
  });
};
const addChoice = (choice) => {
  store.commit('ci/setPreviewerOperation', {
    operations: [
      {
        component: 'choices',
        operation: 'POST',
        details: {
          choice: {
            name: choice.name,
            type: choice.type,
            default: choice.default,
            behaviors: choice.behaviors,
          },
        },
      },
    ],
    ciId: route.params.ciId,
  });
};
</script>

<template>
  <div class="ci-config">
    <div class="flex h-full w-3/5 flex-col gap-6 overflow-y-auto px-6">
      <init />

      <div class="ci-config__choices">
        <h4 class="my-2 px-2">Choices</h4>

        <div v-for="(choice, index) in choices" :key="index" class="ci-config__choice">
          <div class="ci-config__choice__meta" @click="expanded.choice = { choice, operation: 'showBehaviors' }">
            {{ index + 1 }}. {{ choice.name }} ({{ choice.type }})
          </div>
          <button
            type="button"
            :title="`Behaviors for '${choice.name}'`"
            class="bg-white"
            :class="{
              'bg-brand-600 text-white':
                expanded.choice?.choice === choice && expanded.choice?.operation === 'showBehaviors',
            }"
            @click="expanded.choice = { choice, operation: 'showBehaviors' }"
          >
            <list-numbers-icon class="h-5 w-5"></list-numbers-icon> Behaviors
          </button>
          <button
            type="button"
            :title="`Edit ${choice.name}`"
            class="bg-white"
            :class="{
              'bg-brand-500 text-white': expanded.choice?.choice === choice && expanded.choice?.operation === 'edit',
            }"
            @click="expanded.choice = { choice, operation: 'edit' }"
          >
            <edit-icon class="h-5 w-5"></edit-icon> Edit
          </button>
          <button
            type="button"
            :title="`Delete ${choice.name}`"
            class="rounded-r bg-white px-3 py-2 text-red-600 hover:bg-gray-50"
            @click="
              remove(choice);
              expanded.choice = null;
              choices.splice(index, 1);
            "
          >
            <trash-icon class="h-5 w-5"></trash-icon> Delete
          </button>

          <div
            v-if="expanded.choice && expanded.choice.choice === choice"
            class="col-span-3 -mt-2 flex w-full flex-col rounded-b-lg bg-offwhite p-2 text-sm shadow-inner"
          >
            <div v-if="expanded.choice.operation === 'showBehaviors'" class="flex flex-col gap-2">
              <h4 class="mt-2 px-2">{{ choice.name }}'s Behaviors</h4>

              <div
                v-for="(behavior, behaviorIndex) in choice.behaviors"
                :key="`${choice.name}--behavior--{${behaviorIndex}`"
                class="ci-config__choice__behavior flex flex-wrap gap-1 rounded bg-gray-100"
              >
                <input
                  v-model="behavior.name"
                  class="outline-none flex-grow rounded-l bg-white px-3 focus:bg-gray-50"
                  contenteditable="true"
                  spellcheck="false"
                />
                <button
                  type="button"
                  class="px-3 py-2 hover:bg-brand-500 hover:text-white"
                  :class="{
                    'bg-brand-600 text-white': expanded.behavior === behavior,
                    'bg-white text-brand-600': expanded.behavior !== behavior,
                  }"
                  title="Behavior Scripts"
                  @click="expanded.behavior = behavior"
                >
                  <code-icon class="h-4 w-4"></code-icon> Scripts
                </button>
                <button type="button" class="bg-white px-3 py-2 text-green-700 hover:bg-gray-50" title="Play behavior">
                  <player-play-icon class="h-4 w-4"></player-play-icon> Play
                </button>
                <button
                  type="button"
                  class="rounded-r bg-white px-3 py-2 text-red-600 hover:bg-gray-50"
                  title="Delete Behavior"
                  @click="choice.behaviors.splice(behaviorIndex, 1)"
                >
                  <trash-icon class="h-4 w-4"></trash-icon> Delete
                </button>

                <div v-if="expanded.behavior === behavior" class="flex w-full flex-col gap-2 px-4 py-2">
                  {{ expanded.behavior.name }}'s Scripts
                  <code-editor
                    v-if="expanded.behavior"
                    :key="`${expanded.choice.choice.name}-${expanded.behavior.name}__pre-script`"
                    v-model="expanded.behavior.pre"
                    title="Pre Script"
                  />
                  <code-editor
                    v-if="expanded.behavior"
                    :key="`${expanded.choice.choice.name}-${expanded.behavior.name}__on-script`"
                    v-model="expanded.behavior.on"
                    title="On Script"
                  />
                  <code-editor
                    v-if="expanded.behavior"
                    :key="`${expanded.choice.choice.name}-${expanded.behavior.name}__post-script`"
                    v-model="expanded.behavior.post"
                    title="Post Script"
                  />
                </div>
              </div>

              <button
                type="button"
                title="Add Behavior"
                class="flex items-center gap-4 rounded border-4 border-dashed border-white bg-white bg-opacity-40 p-2"
                @click="choice.behaviors.push({ name: 'New Behavior', pre: '', post: '', on: '' })"
              >
                <plus-icon class="h-4 w-4"></plus-icon>
                Add New Behavior
              </button>
            </div>

            <edit-choice v-else :key="choice" :choice-to-update="choice" @update="Object.assign(choice, $event)" />
          </div>
        </div>

        <button
          title="Add Choice"
          type="button"
          @click="
            expanded.behavior = undefined;
            choices.push({ name: `New Choice ${choices.length}`, type: 'string', behaviors: [] });
            expanded.choice = { choice: choices[choices.length - 1], operation: 'edit' };
            addChoice(choices.slice(-1)[0]);
          "
        >
          <plus-icon class="h-5 w-5"></plus-icon>
          Add New Choice
        </button>
      </div>
    </div>

    <div class="relative top-4 flex flex-col gap-8">
      <div><slot /></div>

      <div v-if="expanded.choice">
        <sample-u-i v-if="expanded.choice" :choice="expanded.choice.choice" />
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.ci-config {
  @apply flex h-full w-full gap-y-4;
}

.ci-config__choices {
  @apply flex flex-col;
}
.ci-config__choice {
  @apply flex flex-wrap gap-1 border bg-offwhite;
}
.ci-config__choice__meta {
  @apply flex flex-grow cursor-pointer items-center rounded-l bg-white px-2;
}

button {
  @apply flex items-center gap-2 px-3 py-2;
}

button[title='Add Choice'] {
  @apply gap-4 rounded border-4 border-dashed border-offwhite bg-offwhite bg-opacity-40;
}
</style>
